import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Check, X, Zap } from 'lucide-react';
import { pricingData } from '../mockData';

const Pricing = () => {
  const handleCTA = (planName) => {
    if (planName === 'Enterprise') {
      window.location.href = 'mailto:sales@aiasap.com?subject=Enterprise Plan Inquiry';
    } else {
      window.open('https://calendly.com/aiasap-info/ai-asap-consultation', '_blank');
    }
  };

  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-200 px-4 py-1.5 text-sm font-semibold">
            TRANSPARENT PRICING
          </Badge>
          <h2 className="text-5xl font-bold text-slate-900 mb-6">
            Choose Your Growth Path
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            No hidden fees. No contracts. Cancel anytime. Start with a 14-day free trial.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {pricingData.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative overflow-hidden transition-all duration-300 ${
                plan.popular 
                  ? 'border-4 border-blue-500 shadow-2xl scale-105 lg:scale-110 z-10' 
                  : 'border-2 hover:border-blue-300 hover:shadow-xl'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-2 text-sm font-bold transform rotate-0 translate-x-0 translate-y-0 rounded-bl-lg">
                  <Zap className="inline h-4 w-4 mr-1" />
                  MOST POPULAR
                </div>
              )}

              <CardHeader className="pb-8 pt-10">
                <div className="mb-4">
                  <CardTitle className="text-3xl font-bold text-slate-900 mb-2">
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-slate-600 font-medium">
                    {plan.tagline}
                  </CardDescription>
                </div>

                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    {plan.price === "Custom" ? (
                      <span className="text-5xl font-bold text-slate-900">{plan.price}</span>
                    ) : (
                      <>
                        <span className="text-5xl font-bold text-slate-900">{plan.price}</span>
                        <span className="text-slate-600">/{plan.period.split(' ')[1]}</span>
                      </>
                    )}
                  </div>
                  {(plan.period === 'tailored to your needs' || plan.period === 'tailored pricing') && (
                    <span className="text-slate-600 text-sm">{plan.period}</span>
                  )}
                  <div className="text-sm text-slate-500 mt-2">{plan.setupFee}</div>
                </div>

                <p className="text-slate-600 text-sm leading-relaxed">
                  {plan.description}
                </p>
              </CardHeader>

              <CardContent className="pb-8">
                <Button 
                  onClick={() => handleCTA(plan.name)}
                  className={`w-full mb-6 py-6 text-lg font-semibold rounded-lg transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg hover:shadow-xl hover:scale-105'
                      : 'bg-slate-900 hover:bg-slate-800 text-white hover:scale-105'
                  }`}
                >
                  {plan.cta}
                </Button>

                <div className="space-y-3 mb-4">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="h-5 w-5 text-slate-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${feature.included ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <div className="text-xs text-slate-500">
                    <span className="font-semibold">Usage Limits:</span> {plan.limits}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-2">
            All plans include free onboarding, training, and technical support
          </p>
          <p className="text-sm text-slate-500">
            Need a custom solution? <a href="https://calendly.com/aiasap-info/ai-asap-consultation" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Contact our team</a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
